import { StudioLogger } from '@studio/logger/StudioLogger';

import type { IAgoraRTCClient, NetworkQuality } from 'agora-rtc-sdk-ng';

import { AGORA_NETWORK_QUALITY_TO_STRING_MAP } from './constants';
interface NetworkHealthData {
  // Network quality from Agora (excellent, good, poor, etc.)
  networkQuality: string;
  // Video metrics used in SignalStrengthV2 tooltip
  videoPacketLoss: number;
  videoJitter: number;
  videoLatency: number;
  // Video frame info used in SignalStrengthV2 tooltip
  videoFrame: string;
  receivedFrame: string;
  // Required for identification
  'attendee-id': string;
  isLocalUser: boolean;
}

const LOG = new StudioLogger('NetworkHealthDispatcher');

export class NetworkHealthDispatcher {
  private static instance: NetworkHealthDispatcher | null = null;
  private networkHealthInterval: NodeJS.Timeout | null = null;
  private client!: IAgoraRTCClient;
  private currentAttendeeId!: string;
  // eslint-disable-next-line no-unused-vars
  private dispatch?: (action: any) => void;
  // eslint-disable-next-line no-unused-vars
  private setAttendeeNetworkHealth?: (data: any) => any;
  private localNetworkQuality: NetworkQuality | undefined = undefined;

  /**
   * Private constructor (Singleton)
   */
  private constructor(
    client: IAgoraRTCClient,
    attendeeId: string,
    // eslint-disable-next-line no-unused-vars
    dispatch?: (action: any) => void,
    // eslint-disable-next-line no-unused-vars
    setAttendeeNetworkHealth?: (data: any) => any
  ) {
    this.client = client;
    this.currentAttendeeId = attendeeId;
    this.dispatch = dispatch;
    this.setAttendeeNetworkHealth = setAttendeeNetworkHealth;
    this.client.on('network-quality', this.handleNetworkQuality);
    this.startDispatching();
  }

  /**
   * Bound event handler for network quality changes
   */
  private handleNetworkQuality = (data: NetworkQuality): void => {
    this.localNetworkQuality = data;
  };

  /**
   * Access point for Singleton
   */
  public static getInstance(
    client?: IAgoraRTCClient,
    attendeeId?: string,
    // eslint-disable-next-line no-unused-vars
    dispatch?: (action: any) => void,
    // eslint-disable-next-line no-unused-vars
    setAttendeeNetworkHealth?: (data: any) => any
  ): NetworkHealthDispatcher {
    if (!NetworkHealthDispatcher.instance) {
      if (!client || !attendeeId) {
        throw new Error('NetworkHealthDispatcher: client and attendeeId are required for first initialization');
      }
      NetworkHealthDispatcher.instance = new NetworkHealthDispatcher(
        client,
        attendeeId,
        dispatch,
        setAttendeeNetworkHealth
      );
    }
    return NetworkHealthDispatcher.instance;
  }

  private startDispatching(): void {
    this.networkHealthInterval = setInterval(async () => {
      await this.dispatchNetworkHealthUpdates();
    }, 3000);
  }

  /**
   * Dispatch network health updates to Redux state
   */
  private async dispatchNetworkHealthUpdates(): Promise<void> {
    if (!this.dispatch || !this.setAttendeeNetworkHealth) {
      return;
    }

    try {
      // Calculate network health data for all users (current user + remote users)
      const allNetworkHealthData = await this.calculateAllNetworkHealthData();

      this.dispatch(this.setAttendeeNetworkHealth(allNetworkHealthData));
    } catch (error) {
      LOG.error('Error updating network health from Agora metrics', error);
    }
  }

  /**
   * Calculate network health data for all users (current user + remote users)
   */
  private async calculateAllNetworkHealthData(): Promise<{ [attendeeId: string]: NetworkHealthData }> {
    try {
      const remoteNetworkQualities = this.client.getRemoteNetworkQuality();
      const localVideoStats = this.client.getLocalVideoStats();
      const remoteUsers = this.client.remoteUsers;
      const remoteVideoStats = this.client.getRemoteVideoStats();
      const allNetworkHealthData: { [attendeeId: string]: NetworkHealthData } = {};

      // Calculate for current user (local stats)
      allNetworkHealthData[this.currentAttendeeId] = await this.calculateNetworkHealthDataForUser(
        this.currentAttendeeId,
        true, // isLocalUser
        localVideoStats,
        remoteVideoStats,
        this.localNetworkQuality
      );

      // Calculate for all remote users
      for (const remoteUser of remoteUsers) {
        const remoteAttendeeId = remoteUser.uid.toString();

        // Skip if this is the current user (already calculated above)
        if (remoteAttendeeId === this.currentAttendeeId) {
          continue;
        }

        // Only calculate NHI for users with video on
        if (!remoteUser.hasVideo) {
          continue;
        }

        allNetworkHealthData[remoteAttendeeId] = await this.calculateNetworkHealthDataForUser(
          remoteAttendeeId,
          false, // isLocalUser
          null, // localVideoStats (not available for remote users)
          remoteVideoStats?.[remoteAttendeeId],
          remoteNetworkQualities?.[remoteAttendeeId]
        );
      }

      return allNetworkHealthData;
    } catch (error) {
      LOG.error(
        'agora-calculate-all-network-health-error',
        error,
        'Failed to calculate network health data for all users'
      );
      return {};
    }
  }

  /**
   * Calculate network health data for a specific user
   */
  private async calculateNetworkHealthDataForUser(
    attendeeId: string,
    isLocalUser: boolean,
    localVideoStats: any,
    remoteVideoStats: any,
    remoteNetworkQuality?: any
  ): Promise<NetworkHealthData> {
    try {
      // For local user, use local stats; for remote users, use remote stats
      let effectiveVideoStats;
      let userRemoteVideoStats;

      if (isLocalUser) {
        // Current user - use local stats
        effectiveVideoStats = localVideoStats;
        userRemoteVideoStats = remoteVideoStats;
      } else {
        // Remote user - use their remote stats
        effectiveVideoStats = null; // Remote video stats are limited
        userRemoteVideoStats = remoteVideoStats;
      }

      // Get stats specific to this user
      const userVideoStats = userRemoteVideoStats;

      const networkHealthData: NetworkHealthData = {
        // Network quality from Agora (excellent, good, poor, etc.)
        networkQuality:
          AGORA_NETWORK_QUALITY_TO_STRING_MAP[remoteNetworkQuality?.uplinkNetworkQuality] || 'unavailable',

        // Video metrics used in SignalStrengthV2 tooltip
        videoPacketLoss:
          Math.round(
            (userVideoStats?.packetLossRate || (isLocalUser ? effectiveVideoStats?.currentPacketLossRate : 0) || 0) *
              1000
          ) / 1000,

        videoJitter: Math.round(
          userVideoStats?.jitter ||
            (!isLocalUser && userVideoStats?.transportDelay) ||
            (isLocalUser ? effectiveVideoStats?.sendJitterMs : 0) ||
            0
        ),

        videoLatency:
          Math.round(
            (userVideoStats?.end2EndDelay ||
              userVideoStats?.delay ||
              (isLocalUser ? effectiveVideoStats?.sendRttMs : 0) ||
              0) * 1000
          ) / 1000,

        // Video frame info used in SignalStrengthV2 tooltip
        videoFrame: isLocalUser
          ? this.formatVideoFrame({
              frameRate: effectiveVideoStats?.sendFrameRate || 0,
              width: effectiveVideoStats?.sendResolutionWidth || 0,
              height: effectiveVideoStats?.sendResolutionHeight || 0
            })
          : this.formatReceivedFrame({
              frameRate: userVideoStats?.receiveFrameRate || 0,
              width: userVideoStats?.receiveResolutionWidth || 0,
              height: userVideoStats?.receiveResolutionHeight || 0
            }),

        receivedFrame: this.formatReceivedFrame({
          frameRate: userVideoStats?.receiveFrameRate || 0,
          width: userVideoStats?.receiveResolutionWidth || 0,
          height: userVideoStats?.receiveResolutionHeight || 0
        }),

        // Required for identification
        'attendee-id': attendeeId,
        isLocalUser: isLocalUser
      };

      return networkHealthData;
    } catch (error) {
      LOG.error(
        'agora-network-health-calculation-error',
        error,
        `Failed to calculate network health data for user: ${attendeeId}`
      );

      // Return default values on error
      return {
        networkQuality: 'unavailable',
        videoPacketLoss: 0,
        videoJitter: 0,
        videoLatency: 0,
        videoFrame: 'NA',
        receivedFrame: 'NA',
        'attendee-id': attendeeId,
        isLocalUser: isLocalUser
      };
    }
  }

  /**
   * Format video frame information for display
   */
  private formatVideoFrame({ frameRate, width, height }: { frameRate: number; width: number; height: number }): string {
    if (frameRate === 0 && width === 0 && height === 0) {
      return 'NA';
    }

    return `${width}x${height}, ${frameRate}fps`;
  }

  /**
   * Format received frame information for display
   */
  private formatReceivedFrame({
    frameRate,
    width,
    height
  }: {
    frameRate: number;
    width: number;
    height: number;
  }): string {
    if (frameRate === 0 && width === 0 && height === 0) {
      return 'NA';
    }

    return `${width}x${height}, ${frameRate}fps`;
  }

  stop(): void {
    if (this.networkHealthInterval) {
      clearInterval(this.networkHealthInterval);
      this.networkHealthInterval = null;
    }
    LOG.info('NetworkHealthDispatcher stopped');
  }

  destroy(): void {
    this.stop();
    if (NetworkHealthDispatcher.instance === this) {
      NetworkHealthDispatcher.instance = null;
    }
    LOG.info('NetworkHealthDispatcher destroyed');
  }
}

export default NetworkHealthDispatcher;
